export type WebABAllocationMode = 'delivery_only' | 'experiment';

export type WebABPublicationVariantRole = 'baseline' | 'control' | 'treatment' | 'holdout';

export interface WebABPublicationVariantSummary {
  id: string;
  name: string;
  description?: string | null;
  role?: WebABPublicationVariantRole | null;
  tags?: string[];
  weight: number;
  webPlacementId: string;
  webPlacementAlias: string;
  webPlacementName: string;
}

export interface WebABPublicationRecord {
  id: string;
  revision: number;
  iterationId?: string | null;
  routingHash?: string | null;
  allocationMode?: WebABAllocationMode | null;
  label?: string | null;
  description?: string | null;
  tags?: string[];
  baselineVariantId?: string | null;
  publishedAt: string;
  publishedBy: string;
  gcsPath?: string | null;
  webUrl?: string | null;
  variants: WebABPublicationVariantSummary[];
}

export interface BuildWebABPublicationRecordInput {
  id: string;
  revision: number;
  iterationId: string;
  routingHash: string;
  allocationMode: WebABAllocationMode;
  label: string;
  description?: string | null;
  parentTags?: string[];
  iterationTags?: string[];
  baselineVariantId?: string | null;
  publishedAt: string;
  publishedBy: string;
  gcsPath?: string | null;
  webUrl?: string | null;
  variants: WebABPublicationVariantSummary[];
}

export interface WebABRoutingVariantInput {
  id: string;
  weight: number;
  webPlacementId: string;
  webPlacementAlias: string;
}

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort()
      .map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const fnv1a = (input: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

/** Canonical routing projection shared by UI, CLI, and rollout publication. */
export const normalizeWebABRoutingState = (
  abSalt: string,
  variants: WebABRoutingVariantInput[],
) => ({
  abSalt,
  variants: variants.map(variant => ({
    id: variant.id,
    weight: variant.weight,
    webPlacementId: variant.webPlacementId,
    webPlacementAlias: variant.webPlacementAlias,
  })),
});

export const buildWebABRoutingHash = (
  abSalt: string,
  variants: WebABRoutingVariantInput[],
): string => `fnv1a:${fnv1a(stableStringify(normalizeWebABRoutingState(abSalt, variants)))}`;

export const getWebABAllocationMode = (
  variants: Array<{ weight: number }>,
): WebABAllocationMode => variants.filter(variant => variant.weight > 0).length >= 2
  ? 'experiment'
  : 'delivery_only';

const normalizePublicationTags = (...tagSets: Array<string[] | undefined>): string[] =>
  Array.from(new Set(
    tagSets
      .flatMap(tags => tags ?? [])
      .map(tag => tag.trim())
      .filter(Boolean),
  )).slice(0, 25);

/** Canonical publication audit record shared by UI, CLI, and rollout publishers. */
export const buildWebABPublicationRecord = (
  input: BuildWebABPublicationRecordInput,
): WebABPublicationRecord => ({
  id: input.id,
  revision: input.revision,
  iterationId: input.iterationId,
  routingHash: input.routingHash,
  allocationMode: input.allocationMode,
  label: input.label,
  description: input.description ?? null,
  tags: normalizePublicationTags(input.parentTags, input.iterationTags),
  baselineVariantId: input.baselineVariantId ?? null,
  publishedAt: input.publishedAt,
  publishedBy: input.publishedBy,
  gcsPath: input.gcsPath ?? null,
  webUrl: input.webUrl ?? null,
  variants: input.variants.map(variant => ({
    ...variant,
    description: variant.description ?? null,
    role: variant.role ?? null,
    tags: normalizePublicationTags(variant.tags),
  })),
});

export const buildWebABIterationId = (
  sequence: number,
  routingHash: string,
): string => `iter_${String(sequence).padStart(4, '0')}_${routingHash.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)}`;
